import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useClientAuth } from '../context/ClientAuthContext';
import { 
  House, FileText, Upload, Signature, SignOut, User, Receipt, ListChecks, List, X
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navItems = [
  { path: '/portal/dashboard', label: 'Dashboard', icon: House },
  { path: '/portal/documents', label: 'My Documents', icon: FileText },
  { path: '/portal/upload', label: 'Upload Files', icon: Upload },
  { path: '/portal/invoices', label: 'Sales Invoices', icon: Receipt },
  { path: '/portal/forms', label: 'Forms & Documents', icon: ListChecks },
  { path: '/portal/signature', label: 'Sign Documents', icon: Signature },
];

export default function ClientLayout() {
  const { client, logout } = useClientAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={closeSidebar} data-testid="portal-sidebar-overlay" />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:sticky top-0 left-0 h-screen z-50 md:z-auto
        w-64 border-r border-border bg-card flex flex-col
        transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" weight="bold" />
            </div>
            <div>
              <span className="font-semibold text-sm tracking-tight block" style={{ fontFamily: 'Manrope, sans-serif' }}>Client Portal</span>
              <span className="text-xs text-muted-foreground">Applegrow</span>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="md:hidden" onClick={closeSidebar} data-testid="portal-close-sidebar">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-2 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                    isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                  }`
                }
                data-testid={`portal-nav-${item.path.split('/').pop()}`}
              >
                <item.icon className="w-5 h-5" weight="regular" />
                <span className="text-sm font-medium">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 px-3" data-testid="portal-user-menu">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">{client?.name?.[0]?.toUpperCase() || 'C'}</span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium truncate">{client?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{client?.email}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleLogout} data-testid="portal-logout-btn">
                <SignOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-screen w-full md:w-auto">
        {/* Mobile top bar */}
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-4 sticky top-0 z-30 md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="portal-sidebar-toggle">
            <List className="w-5 h-5" />
          </Button>
          <span className="text-sm font-medium">Client Portal</span>
          <div className="w-10" />
        </header>

        {/* Page content */}
        <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
